import './style.css'
import * as THREE from 'three'
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader.js'
import {OrbitControls} from "three/examples/jsm/controls/OrbitControls"
import {VRButton} from 'three/examples/jsm/webxr/VRButton.js'
import {XRControllerModelFactory} from "three/examples/jsm/webxr/XRControllerModelFactory";
import helvetiker from 'three/examples/fonts/helvetiker_regular.typeface.json'


/**
 * Variables declaration
 */

const intersected = []
const tempMatrix = new THREE.Matrix4()
const clock = new THREE.Clock()
let currentIntersect = null
let letterIntersect = null

let camera, scene, renderer
let controller1, controller2
let controllerGrip1, controllerGrip2

let raycaster
let controls
let cursor
const TPs = []
let tpMaterial
let tpMaterialHover
let dolly
const cameraOffset = 0.05
let cameraDirection = new THREE.Vector3()
let textureLoader
let font
let textLetters = []
let textFinalPosition
const foundSound = new Audio('sounds/ding.mp3')
foundSound.volume = 0.5
const foundAllSound = new Audio('sounds/success.mp3')
foundAllSound.volume = 0.5

let foundLetters = []
let isLetterGameOver = false
let instructionText, arrowHelper
let mixerFox = null
let mixerStag = null
let previousTime = 0
let spawn



init()
animate()

/**
 * Move camera while being in normal mode
 */
function transformCamera(tp) {
  camera.getWorldDirection(cameraDirection)
  cameraDirection.normalize()
  const vec = new THREE.Vector3(tp.position.x + cameraDirection.x * cameraOffset, tp.position.y + cameraDirection.y * cameraOffset, tp.position.z + cameraDirection.z * cameraOffset)
  camera.position.copy(tp.position)
  controls.target.copy(vec)
}

/**
 * Move camera while being in VR mode
 */
function transformCameraVR(tp) {
  TPs.forEach(element => element.visible = true)
  tp.visible = false
  camera.getWorldDirection(cameraDirection)
  cameraDirection.normalize()
  const vec = new THREE.Vector3(tp.position.x + cameraDirection.x * cameraOffset, tp.position.y + cameraDirection.y * cameraOffset, tp.position.z + cameraDirection.z * cameraOffset)
  controls.target.copy(vec)
  dolly.position.copy(tp.position)
  if(tp.position.y <= 1.5) {
    dolly.position.setY(0)
  } else {
    dolly.position.setY(10)
  }

}

/**
 * Move the letter when found and play a sound
 */
function findLetter(letter) {
  letter.position.copy(textFinalPosition[letter.name])
  letter.rotation.y = Math.PI * 0.5

  if (foundLetters.includes(false) && !foundLetters[letter.name]) {
    foundLetters[letter.name] = true
    foundSound.currentTime = 0
    foundSound.play()
  }
  if (!foundLetters.includes(false) && !isLetterGameOver) {
    isLetterGameOver = true
    foundAllSound.play()
  }
}

/**
 * Load the world
 */
function init() {

  // Create a group to move the VR camera
  dolly = new THREE.Group()

  font = new THREE.Font(helvetiker)

  // Retrieve the canvas
  const canvas = document.querySelector('canvas.webgl')

  // Create the scene
  scene = new THREE.Scene()

  // Create the light the three.js elements
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.6)
  scene.add(ambientLight)

  // Get the screen sizes
  const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
  }

  //Update the sizes when the screen is resized. If it's not done, the size of the simulation is fixed
  window.addEventListener('resize', () => {
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  })


  // Create the camera
  camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
  camera.position.set(0, 2, 20)
  scene.add(camera)

  // Create the controls
  controls = new OrbitControls(camera, canvas)
  controls.enableDamping = true
  controls.rotateSpeed *= -0.5;


  raycaster = new THREE.Raycaster()
  cursor = new THREE.Vector2()

  // Triggered when the mouse is moving. Store it's position
  window.addEventListener('mousemove', (e) => {
    cursor.x = e.clientX / sizes.width * 2 - 1
    cursor.y = -(e.clientY / sizes.height) * 2 + 1
  })

  // Triggered when the mouse is clicked. Used to detect if a tp point is clicked or if a letter has been found
  window.addEventListener('click', () => {
    if (currentIntersect) {
      hideHelp()
      transformCamera(currentIntersect.object)
    }
    if (letterIntersect) {
      findLetter(letterIntersect.object)
    }
  })

  // Triggered when the user takes his finger off the screen
  window.addEventListener('touchend', () => {
    if (currentIntersect) {
      hideHelp()
      transformCamera(currentIntersect.object)
    }
    if (letterIntersect) {
      findLetter(letterIntersect.object)
    }
    cursor.x = -20
    cursor.y = -20
  })

  // Triggered when the user's finger touches the screen. Stores it's coordinates
  window.addEventListener('touchstart', (e) => {
    for (let i = 0; i < e.targetTouches.length; i++) {
      cursor.x = e.targetTouches[i].clientX / sizes.width * 2 - 1
      cursor.y = -(e.targetTouches[i].clientY / sizes.height) * 2 + 1
    }
  })

  // Create the renderer
  renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true
  })
  renderer.setSize(sizes.width, sizes.height)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  renderer.outputEncoding = THREE.sRGBEncoding
  renderer.xr.enabled = true;

  // Add the VR button to the html
  document.body.appendChild(VRButton.createButton(renderer))

  renderer.xr.addEventListener( 'sessionstart', function ( event ) {
    transformCameraVR(spawn)
  } );

  addTpPoints()
  loadModels()
  toggleHelp()
  createFrames()
  initGameLetters()
  addControllers()
}

/**
 * Add and place the points of teleportation
 */
function addTpPoints() {
  const height = 1.5

  const tpGeometry = new THREE.SphereBufferGeometry(0.2, 16, 16)
  tpMaterial = new THREE.MeshBasicMaterial({color: '#ff0000'})
  tpMaterialHover = new THREE.MeshBasicMaterial({color: '#fff'})


  spawn = new THREE.Mesh(tpGeometry, tpMaterial)
  spawn.position.set(0, height, 18)

  const spawn1 = new THREE.Mesh(tpGeometry, tpMaterial)
  spawn1.position.set(0, height, 12)

  const midPole1 = new THREE.Mesh(tpGeometry, tpMaterial)
  midPole1.position.set(0, height, 5)

  const frontPole = new THREE.Mesh(tpGeometry, tpMaterial)
  frontPole.position.set(2.5, height, 1)

  const midPole2 = new THREE.Mesh(tpGeometry, tpMaterial)
  midPole2.position.set(-2, height, -5)


  const midPole4 = new THREE.Mesh(tpGeometry, tpMaterial)
  midPole4.position.set(-5, height, 0)

  const leftPole = new THREE.Mesh(tpGeometry, tpMaterial)
  leftPole.position.set(-12, height, 0)

  const rightPole = new THREE.Mesh(tpGeometry, tpMaterial)
  rightPole.position.set(12, height, 0)

  const watchTowerFront = new THREE.Mesh(tpGeometry, tpMaterial)
  watchTowerFront.position.set(-5.3, 11.5, -14)

  const watchTowerBack = new THREE.Mesh(tpGeometry, tpMaterial)
  watchTowerBack.position.set(-5.3, 11.5, -19)

  const watchTowerLeft = new THREE.Mesh(tpGeometry, tpMaterial)
  watchTowerLeft.position.set(-8.3, 11.5, -16)

  const watchTowerRight = new THREE.Mesh(tpGeometry, tpMaterial)
  watchTowerRight.position.set(-2.3, 11.5, -16)

  const houseProjectEntrance = new THREE.Mesh(tpGeometry, tpMaterial)
  houseProjectEntrance.position.set(-14, height, 9.5)

  const houseProjectInside = new THREE.Mesh(tpGeometry, tpMaterial)
  houseProjectInside.position.set(-13, height, 13)

  const houseMemberEntrance = new THREE.Mesh(tpGeometry, tpMaterial)
  houseMemberEntrance.position.set(-11.8, height, -8.5)

  const houseMemberInside = new THREE.Mesh(tpGeometry, tpMaterial)
  houseMemberInside.position.set(-13, height, -12)

  const houseadvancedEntrance = new THREE.Mesh(tpGeometry, tpMaterial)
  houseadvancedEntrance.position.set(15.5, height, -8.5)

  const houseadvancedInside = new THREE.Mesh(tpGeometry, tpMaterial)
  houseadvancedInside.position.set(14.5, height, -13)

  const houseAdvancedEntrance = new THREE.Mesh(tpGeometry, tpMaterial)
  houseAdvancedEntrance.position.set(9.8, height, 11.5)

  const houseAdvancedInside = new THREE.Mesh(tpGeometry, tpMaterial)
  houseAdvancedInside.position.set(11, height, 15)

  TPs.push(
      spawn, spawn1,
      midPole1, midPole2, midPole4, frontPole,
      leftPole, rightPole,
      watchTowerFront, watchTowerBack, watchTowerLeft, watchTowerRight,
      houseProjectEntrance, houseProjectInside,
      houseMemberEntrance, houseMemberInside,
      houseadvancedEntrance, houseadvancedInside,
      houseAdvancedEntrance, houseAdvancedInside
  )

  TPs.forEach(tpPoint => scene.add(tpPoint));

  transformCamera(spawn)

}

/**
 * Load every models and their texture
 */
function loadModels() {

  // Load the world background
  const cubeTextureLoader = new THREE.CubeTextureLoader()
  const environmentMap = cubeTextureLoader.load([
    'cubeMap/px.png',
    'cubeMap/nx.png',
    'cubeMap/py.png',
    'cubeMap/ny.png',
    'cubeMap/pz.png',
    'cubeMap/nz.png',
  ])

  environmentMap.encoding = THREE.sRGBEncoding
  scene.background = environmentMap

  // Load the world and the animals
  textureLoader = new THREE.TextureLoader()

  const TR = textureLoader.load('models/TR.jpg')
  TR.flipY = false
  TR.encoding = THREE.sRGBEncoding
  const TL = textureLoader.load('models/TL.jpg')
  TL.flipY = false
  TL.encoding = THREE.sRGBEncoding
  const BR = textureLoader.load('models/BR.jpg')
  BR.flipY = false
  BR.encoding = THREE.sRGBEncoding
  const BL = textureLoader.load('models/BL.jpg')
  BL.flipY = false
  BL.encoding = THREE.sRGBEncoding

  const TRTexture = new THREE.MeshBasicMaterial({map: TR})
  const TLTexture = new THREE.MeshBasicMaterial({map: TL})
  const BRTexture = new THREE.MeshBasicMaterial({map: BR})
  const BLTexture = new THREE.MeshBasicMaterial({map: BL})

// GLTF loader
  const gltfLoader = new GLTFLoader()

  gltfLoader.load(
      'fox/glTF/Fox.gltf',
      (gltf) => {
        mixerFox = new THREE.AnimationMixer(gltf.scene)
        const action = mixerFox.clipAction(gltf.animations[0])
        action.play()
        gltf.scene.scale.set(0.01, 0.01, 0.01)
        gltf.scene.position.set(4, 0, -10)
        gltf.scene.rotation.y = Math.PI * 0.1
        scene.add(gltf.scene)
      }
  )

  gltfLoader.load(
      'stag/Stag.gltf',
      (gltf) => {
        mixerStag = new THREE.AnimationMixer(gltf.scene)
        const action = mixerStag.clipAction(gltf.animations[3])
        action.play()
        gltf.scene.scale.set(0.8, 0.8, 0.8)
        gltf.scene.position.set(-1, 0, -17)
        gltf.scene.rotation.y = Math.PI * -0.2
        scene.add(gltf.scene)
      }
  )

  gltfLoader.load(
      'models/TL.glb',
      (gltf) => {
        gltf.scene.traverse((child) => {
          child.material = TLTexture
        })
        scene.add(gltf.scene)
      }
  )
  gltfLoader.load(
      'models/TR.glb',
      (gltf) => {
        gltf.scene.traverse((child) => {
          child.material = TRTexture
        })
        scene.add(gltf.scene)
      }
  )
  gltfLoader.load(
      'models/BL.glb',
      (gltf) => {
        gltf.scene.traverse((child) => {
          child.material = BLTexture
        })
        gltf.scene.position.z = -0.01
        scene.add(gltf.scene)
      }
  )
  gltfLoader.load(
      'models/BR.glb',
      (gltf) => {
        gltf.scene.traverse((child) => {
          child.material = BRTexture
        })
        gltf.scene.position.z = -0.01
        scene.add(gltf.scene)
      }
  )
}

/**
 * Display the world instruction and the TP instruction
 */
function toggleHelp() {
  const fontParams = {
    font: font,
    size: 0.2,
    height: 0.1,
    curveSegments: 5,
    bevelEnabled: true,
    bevelThickness: 0.03,
    bevelSize: 0.02,
    bevelOffset: 0,
    bevelSegments: 4
  }

  const clickMe = new THREE.TextBufferGeometry('Click me', fontParams)
  clickMe.center()
  const textMaterial = new THREE.MeshNormalMaterial()
  instructionText = new THREE.Mesh(clickMe, textMaterial)
  instructionText.position.set(0,2.4,12)
  scene.add(instructionText)

  const dir = new THREE.Vector3( 0, -1, 0 );

  //normalize the direction vector (convert to vector of length 1)
  dir.normalize();

  const origin = new THREE.Vector3( 0, 2.2, 12 );
  const length = 0.4;
  const hex = 0xffffff;
  const headLength = 0.2
  const headWidth = 0.2

  arrowHelper = new THREE.ArrowHelper( dir, origin, length, hex, headLength, headWidth );

  scene.add( arrowHelper );
}

/**
 * Hide the world instruction and the TP instruction
 */
function hideHelp() {
  instructionText.visible = false
  arrowHelper.visible = false
}

/**
 * Create all frames and add their textures
 */
function createFrames() {

  let clone

  const planeGeometry = new THREE.PlaneBufferGeometry(2, 1)
  const planeGeometryHouse = new THREE.PlaneBufferGeometry(1, 0.5)

  const instruction = textureLoader.load('frames/instructions.png')
  instruction.encoding = THREE.sRGBEncoding
  const presentationTexture1 = textureLoader.load('frames/presentation1.png')
  presentationTexture1.encoding = THREE.sRGBEncoding
  const presentationTexture2 = textureLoader.load('frames/presentation2.png')
  presentationTexture2.encoding = THREE.sRGBEncoding
  const presentationTexture3 = textureLoader.load('frames/presentation3.png')
  presentationTexture3.encoding = THREE.sRGBEncoding

  const matInstruction = new THREE.MeshBasicMaterial({map: instruction})
  const blankFrame = new THREE.MeshBasicMaterial({color: '#ffffff'})

  const instructionFrame = new THREE.Mesh(planeGeometry, matInstruction)
  instructionFrame.position.set(2, 1.5, 16)
  instructionFrame.rotateY(Math.PI * -0.3)
  clone = instructionFrame.clone()
  clone.rotateY( Math.PI )
  clone.material = blankFrame
  scene.add(instructionFrame,clone)

  const matPresentation1 = new THREE.MeshBasicMaterial({map: presentationTexture1})
  const presentation1 = new THREE.Mesh(planeGeometry, matPresentation1)
  presentation1.position.set(0.7, 1.5, -1.4)
  presentation1.rotateY(Math.PI * 0.2)
  clone = presentation1.clone()
  clone.rotateY( Math.PI )
  clone.material = blankFrame
  scene.add(presentation1, clone)

  const matPresentation2 = new THREE.MeshBasicMaterial({map: presentationTexture2})
  const presentation2 = new THREE.Mesh(planeGeometry, matPresentation2)
  presentation2.position.set(2.5, 1.5, -2)
  clone = presentation2.clone()
  clone.rotateY( Math.PI )
  clone.material = blankFrame
  scene.add(presentation2, clone)

  const matPresentation3 = new THREE.MeshBasicMaterial({map: presentationTexture3})
  const presentation3 = new THREE.Mesh(planeGeometry, matPresentation3)
  presentation3.position.set(4.3, 1.5, -1.4)
  presentation3.rotateY(Math.PI * -0.2)
  clone = presentation3.clone()
  clone.rotateY( Math.PI )
  clone.material = blankFrame
  scene.add(presentation3, clone)

  const houseMembersTexture = textureLoader.load('frames/houseMembers.png')
  houseMembersTexture.encoding = THREE.sRGBEncoding
  const houseProjectsTexture = textureLoader.load('frames/houseProjects.png')
  houseProjectsTexture.encoding = THREE.sRGBEncoding
  const houseEmpathicTexture = textureLoader.load('frames/houseEmpathic.png')
  houseEmpathicTexture.encoding = THREE.sRGBEncoding
  const houseAdvancedTexture = textureLoader.load('frames/houseAdvanced.png')
  houseAdvancedTexture.encoding = THREE.sRGBEncoding

  const matHouseMembers = new THREE.MeshBasicMaterial({map: houseMembersTexture})
  const houseMembers = new THREE.Mesh(planeGeometryHouse, matHouseMembers)
  houseMembers.position.set(-11.9, 2.5, -8.7)
  scene.add(houseMembers)

  const matHouseProjects = new THREE.MeshBasicMaterial({map: houseProjectsTexture})
  const houseProjects = new THREE.Mesh(planeGeometryHouse, matHouseProjects)
  houseProjects.rotateY(Math.PI)
  houseProjects.position.set(-14, 2.5, 9.84)
  scene.add(houseProjects)

  const matHouseAdvanced = new THREE.MeshBasicMaterial({map: houseAdvancedTexture})
  const houseAdvanced = new THREE.Mesh(planeGeometryHouse, matHouseAdvanced)
  houseAdvanced.rotateY(Math.PI)
  houseAdvanced.position.set(9.85, 2.5, 11.88)
  scene.add(houseAdvanced)

  const matHouseEmpathic = new THREE.MeshBasicMaterial({map: houseEmpathicTexture})
  const houseEmpathic = new THREE.Mesh(planeGeometryHouse, matHouseEmpathic)
  houseEmpathic.position.set(15.6, 2.5, -9.46)
  scene.add(houseEmpathic)

  const frameMember1 = textureLoader.load('frames/member1.png')
  frameMember1.encoding = THREE.sRGBEncoding
  const frameMember2 = textureLoader.load('frames/member2.png')
  frameMember2.encoding = THREE.sRGBEncoding
  const frameMember3 = textureLoader.load('frames/member3.png')
  frameMember3.encoding = THREE.sRGBEncoding
  const frameMember4 = textureLoader.load('frames/member4.png')
  frameMember4.encoding = THREE.sRGBEncoding
  const frameMember5 = textureLoader.load('frames/member5.png')
  frameMember5.encoding = THREE.sRGBEncoding
  const frameMember6 = textureLoader.load('frames/member6.png')
  frameMember6.encoding = THREE.sRGBEncoding
  const frameMember7 = textureLoader.load('frames/member7.png')
  frameMember7.encoding = THREE.sRGBEncoding
  const frameMember8 = textureLoader.load('frames/member8.png')
  frameMember8.encoding = THREE.sRGBEncoding

  const frameProject1 = textureLoader.load('frames/project1.png')
  frameProject1.encoding = THREE.sRGBEncoding
  const frameProject2 = textureLoader.load('frames/project2.png')
  frameProject2.encoding = THREE.sRGBEncoding
  const frameProject3 = textureLoader.load('frames/project3.png')
  frameProject3.encoding = THREE.sRGBEncoding
  const frameProject4 = textureLoader.load('frames/project4.png')
  frameProject4.encoding = THREE.sRGBEncoding
  const frameProject5 = textureLoader.load('frames/project5.png')
  frameProject5.encoding = THREE.sRGBEncoding
  const frameProject6 = textureLoader.load('frames/project6.png')
  frameProject6.encoding = THREE.sRGBEncoding
  const frameProject7 = textureLoader.load('frames/project7.png')
  frameProject7.encoding = THREE.sRGBEncoding
  const frameProject8 = textureLoader.load('frames/project8.png')
  frameProject8.encoding = THREE.sRGBEncoding

  const frameEmpathic1 = textureLoader.load('frames/empathic1.png')
  frameEmpathic1.encoding = THREE.sRGBEncoding
  const frameEmpathic2 = textureLoader.load('frames/empathic2.png')
  frameEmpathic2.encoding = THREE.sRGBEncoding
  const frameEmpathic3 = textureLoader.load('frames/empathic3.png')
  frameEmpathic3.encoding = THREE.sRGBEncoding
  const frameEmpathic4 = textureLoader.load('frames/empathic4.png')
  frameEmpathic4.encoding = THREE.sRGBEncoding
  const frameEmpathic5 = textureLoader.load('frames/empathic5.png')
  frameEmpathic5.encoding = THREE.sRGBEncoding
  const frameEmpathic6 = textureLoader.load('frames/empathic6.png')
  frameEmpathic6.encoding = THREE.sRGBEncoding
  const frameEmpathic7 = textureLoader.load('frames/empathic7.png')
  frameEmpathic7.encoding = THREE.sRGBEncoding
  const frameEmpathic8 = textureLoader.load('frames/empathic8.png')
  frameEmpathic8.encoding = THREE.sRGBEncoding

  const frameAdvanced1 = textureLoader.load('frames/advanced1.png')
  frameAdvanced1.encoding = THREE.sRGBEncoding
  const frameAdvanced2 = textureLoader.load('frames/advanced2.png')
  frameAdvanced2.encoding = THREE.sRGBEncoding
  const frameAdvanced3 = textureLoader.load('frames/advanced3.png')
  frameAdvanced3.encoding = THREE.sRGBEncoding
  const frameAdvanced4 = textureLoader.load('frames/advanced4.png')
  frameAdvanced4.encoding = THREE.sRGBEncoding
  const frameAdvanced5 = textureLoader.load('frames/advanced5.png')
  frameAdvanced5.encoding = THREE.sRGBEncoding
  const frameAdvanced6 = textureLoader.load('frames/advanced6.png')
  frameAdvanced6.encoding = THREE.sRGBEncoding
  const frameAdvanced7 = textureLoader.load('frames/advanced7.png')
  frameAdvanced7.encoding = THREE.sRGBEncoding
  const frameAdvanced8 = textureLoader.load('frames/advanced8.png')
  frameAdvanced8.encoding = THREE.sRGBEncoding

  const projectGroup = new THREE.Group()
  const memberGroup = new THREE.Group()
  const advancedGroup = new THREE.Group()
  const empathicGroup = new THREE.Group()

  const matMember1 = new THREE.MeshBasicMaterial({map: frameMember1})
  const matMember2 = new THREE.MeshBasicMaterial({map: frameMember2})
  const matMember3 = new THREE.MeshBasicMaterial({map: frameMember3})
  const matMember4 = new THREE.MeshBasicMaterial({map: frameMember4})
  const matMember5 = new THREE.MeshBasicMaterial({map: frameMember5})
  const matMember6 = new THREE.MeshBasicMaterial({map: frameMember6})
  const matMember7 = new THREE.MeshBasicMaterial({map: frameMember7})
  const matMember8 = new THREE.MeshBasicMaterial({map: frameMember8})

  const matProject1 = new THREE.MeshBasicMaterial({map: frameProject1})
  const matProject2 = new THREE.MeshBasicMaterial({map: frameProject2})
  const matProject3 = new THREE.MeshBasicMaterial({map: frameProject3})
  const matProject4 = new THREE.MeshBasicMaterial({map: frameProject4})
  const matProject5 = new THREE.MeshBasicMaterial({map: frameProject5})
  const matProject6 = new THREE.MeshBasicMaterial({map: frameProject6})
  const matProject7 = new THREE.MeshBasicMaterial({map: frameProject7})
  const matProject8 = new THREE.MeshBasicMaterial({map: frameProject8})

  const matEmpathic1 = new THREE.MeshBasicMaterial({map: frameEmpathic1})
  const matEmpathic2 = new THREE.MeshBasicMaterial({map: frameEmpathic2})
  const matEmpathic3 = new THREE.MeshBasicMaterial({map: frameEmpathic3})
  const matEmpathic4 = new THREE.MeshBasicMaterial({map: frameEmpathic4})
  const matEmpathic5 = new THREE.MeshBasicMaterial({map: frameEmpathic5})
  const matEmpathic6 = new THREE.MeshBasicMaterial({map: frameEmpathic6})
  const matEmpathic7 = new THREE.MeshBasicMaterial({map: frameEmpathic7})
  const matEmpathic8 = new THREE.MeshBasicMaterial({map: frameEmpathic8})

  const matAdvanced1 = new THREE.MeshBasicMaterial({map: frameAdvanced1})
  const matAdvanced2 = new THREE.MeshBasicMaterial({map: frameAdvanced2})
  const matAdvanced3 = new THREE.MeshBasicMaterial({map: frameAdvanced3})
  const matAdvanced4 = new THREE.MeshBasicMaterial({map: frameAdvanced4})
  const matAdvanced5 = new THREE.MeshBasicMaterial({map: frameAdvanced5})
  const matAdvanced6 = new THREE.MeshBasicMaterial({map: frameAdvanced6})
  const matAdvanced7 = new THREE.MeshBasicMaterial({map: frameAdvanced7})
  const matAdvanced8 = new THREE.MeshBasicMaterial({map: frameAdvanced8})

  const member1 = new THREE.Mesh(planeGeometry, matMember1)
  member1.position.set(-15.65, 1.5, -10.5)
  member1.rotateY(Math.PI * 0.5)

  const member2 = new THREE.Mesh(planeGeometry, matMember2)
  member2.position.set(-15.65, 1.5, -13.5)
  member2.rotateY(Math.PI * 0.5)

  const member3 = new THREE.Mesh(planeGeometry, matMember3)
  member3.position.set(-10.86, 1.5, -10.5)
  member3.rotateY(Math.PI * -0.5)

  const member4 = new THREE.Mesh(planeGeometry, matMember4)
  member4.position.set(-10.86, 1.5, -13.5)
  member4.rotateY(Math.PI * -0.5)

  const member5 = new THREE.Mesh(planeGeometry, matMember5)
  member5.position.set(-12.1, 1.5, -14.75)

  const member6 = new THREE.Mesh(planeGeometry, matMember6)
  member6.position.set(-14.4, 1.5, -14.75)

  const member7 = new THREE.Mesh(planeGeometry, matMember7)
  member7.position.set(-12.1, 2.7, -14.75)

  const member8 = new THREE.Mesh(planeGeometry, matMember8)
  member8.position.set(-14.4, 2.7, -14.75)

  memberGroup.add(member1, member2, member3, member4, member5, member6, member7, member8)

  const project1 = new THREE.Mesh(planeGeometry, matProject1)
  project1.position.set(-15, 1.5, 14.5)
  project1.rotateY(Math.PI * 0.5)

  const project2 = new THREE.Mesh(planeGeometry, matProject2)
  project2.position.set(-15, 1.5, 11.5)
  project2.rotateY(Math.PI * 0.5)

  const project3 = new THREE.Mesh(planeGeometry, matProject3)
  project3.position.set(-10.25, 1.5, 14.5)
  project3.rotateY(Math.PI * -0.5)

  const project4 = new THREE.Mesh(planeGeometry, matProject4)
  project4.position.set(-10.25, 1.5, 11.5)
  project4.rotateY(Math.PI * -0.5)

  const project5 = new THREE.Mesh(planeGeometry, matProject5)
  project5.position.set(-11.5, 1.5, 15.85)
  project5.rotateY(Math.PI)

  const project6 = new THREE.Mesh(planeGeometry, matProject6)
  project6.position.set(-13.75, 1.5, 15.85)
  project6.rotateY(Math.PI)

  const project7 = new THREE.Mesh(planeGeometry, matProject7)
  project7.position.set(-11.5, 2.7, 15.85)
  project7.rotateY(Math.PI)

  const project8 = new THREE.Mesh(planeGeometry, matProject8)
  project8.position.set(-13.75, 2.7, 15.85)
  project8.rotateY(Math.PI)

  projectGroup.add(project1, project2, project3, project4, project5, project6, project7, project8)


  const advanced1 = new THREE.Mesh(planeGeometry, matAdvanced1)
  advanced1.position.set(8.85, 1.5, 16.5)
  advanced1.rotateY(Math.PI * 0.5)

  const advanced2 = new THREE.Mesh(planeGeometry, matAdvanced2)
  advanced2.position.set(8.85, 1.5, 13.75)
  advanced2.rotateY(Math.PI * 0.5)

  const advanced3 = new THREE.Mesh(planeGeometry, matAdvanced3)
  advanced3.position.set(13.6, 1.5, 13.75)
  advanced3.rotateY(Math.PI * -0.5)

  const advanced4 = new THREE.Mesh(planeGeometry, matAdvanced4)
  advanced4.position.set(13.6, 1.5, 16.5)
  advanced4.rotateY(Math.PI * -0.5)

  const advanced5 = new THREE.Mesh(planeGeometry, matAdvanced5)
  advanced5.position.set(10.1, 1.5, 17.9)
  advanced5.rotateY(Math.PI)

  const advanced6 = new THREE.Mesh(planeGeometry, matAdvanced6)
  advanced6.position.set(12.35, 1.5, 17.9)
  advanced6.rotateY(Math.PI)

  const advanced7 = new THREE.Mesh(planeGeometry, matAdvanced7)
  advanced7.position.set(10.1, 2.7, 17.9)
  advanced7.rotateY(Math.PI)

  const advanced8 = new THREE.Mesh(planeGeometry, matAdvanced8)
  advanced8.position.set(12.35, 2.7, 17.9)
  advanced8.rotateY(Math.PI)

  advancedGroup.add(advanced1, advanced2, advanced3, advanced4, advanced5, advanced6, advanced7, advanced8)


  const empathic1 = new THREE.Mesh(planeGeometry, matEmpathic1)
  empathic1.position.set(11.84, 1.5, -14)
  empathic1.rotateY(Math.PI * 0.5)

  const empathic2 = new THREE.Mesh(planeGeometry, matEmpathic2)
  empathic2.position.set(11.84, 1.5, -11.25)
  empathic2.rotateY(Math.PI * 0.5)

  const empathic3 = new THREE.Mesh(planeGeometry, matEmpathic3)
  empathic3.position.set(16.63, 1.5, -11.25)
  empathic3.rotateY(Math.PI * -0.5)

  const empathic4 = new THREE.Mesh(planeGeometry, matEmpathic4)
  empathic4.position.set(16.63, 1.5, -14)
  empathic4.rotateY(Math.PI * -0.5)

  const empathic5 = new THREE.Mesh(planeGeometry, matEmpathic5)
  empathic5.position.set(13.1, 1.5, -15.45)

  const empathic6 = new THREE.Mesh(planeGeometry, matEmpathic6)
  empathic6.position.set(15.35, 1.5, -15.45)

  const empathic7 = new THREE.Mesh(planeGeometry, matEmpathic7)
  empathic7.position.set(13.1, 2.7, -15.45)

  const empathic8 = new THREE.Mesh(planeGeometry, matEmpathic8)
  empathic8.position.set(15.35, 2.7, -15.45)

  empathicGroup.add(empathic1, empathic2, empathic3, empathic4, empathic5, empathic6, empathic7, empathic8)

  scene.add(memberGroup, projectGroup, advancedGroup, empathicGroup)
}

/**
 * Place the letters for the game
 */
function initGameLetters() {

  textFinalPosition = [
    new THREE.Vector3(-2, 2, 18),
    new THREE.Vector3(-2, 2, 17.6),
    new THREE.Vector3(-2, 2, 17.2),
    new THREE.Vector3(-2, 2, 16.8),
    new THREE.Vector3(-2, 2, 16.4),
    new THREE.Vector3(-2, 1.5, 18),
    new THREE.Vector3(-2, 1.5, 17.6),
    new THREE.Vector3(-2, 1.5, 17.2),
    new THREE.Vector3(-2, 1.5, 16.8)
  ]

  const textInitialPosition = [
    new THREE.Vector3(-1.5, 0.8, -12),
    new THREE.Vector3(-11, 0.1, 15),
    new THREE.Vector3(-11.5, 0.1, -14),
    new THREE.Vector3(16, 0.1, -15),
    new THREE.Vector3(13, 0.1, 17),
    new THREE.Vector3(-7, 9.7, -17.5),
    new THREE.Vector3(-16, 1.5, -19),
    new THREE.Vector3(18, 0.1, -17),
    new THREE.Vector3(-10, 0.5, 18)
  ]

  foundLetters = [false, false, false, false, false, false, false, false]

  const fontParams = {
    font: font,
    size: 0.4,
    height: 0.1,
    curveSegments: 5,
    bevelEnabled: true,
    bevelThickness: 0.03,
    bevelSize: 0.02,
    bevelOffset: 0,
    bevelSegments: 4
  }
  const H1 = new THREE.TextBufferGeometry('H', fontParams)
  const U = new THREE.TextBufferGeometry('U', fontParams)
  const M = new THREE.TextBufferGeometry('M', fontParams)
  const A = new THREE.TextBufferGeometry('A', fontParams)
  const N = new THREE.TextBufferGeometry('N', fontParams)

  const T = new THREE.TextBufferGeometry('T', fontParams)
  const E = new THREE.TextBufferGeometry('E', fontParams)
  const C = new THREE.TextBufferGeometry('C', fontParams)
  const H2 = new THREE.TextBufferGeometry('H', fontParams)

  const textMaterial = new THREE.MeshNormalMaterial()

  textLetters = [
    new THREE.Mesh(H1, textMaterial),
    new THREE.Mesh(U, textMaterial),
    new THREE.Mesh(M, textMaterial),
    new THREE.Mesh(A, textMaterial),
    new THREE.Mesh(N, textMaterial),
    new THREE.Mesh(T, textMaterial),
    new THREE.Mesh(E, textMaterial),
    new THREE.Mesh(C, textMaterial),
    new THREE.Mesh(H2, textMaterial)
  ]


  textLetters.forEach((element, index) => {
    element.position.copy(textInitialPosition[index])
    element.name = index
    element.rotateY(Math.PI * Math.random())
    scene.add(element)
  })

}

/**
 * Setup the VR controllers
 */
function addControllers() {
  // controllers

  controller1 = renderer.xr.getController(0)
  controller1.addEventListener('selectstart', onSelectStart)
  controller1.addEventListener('selectend', onSelectEnd)
  scene.add(controller1);

  controller2 = renderer.xr.getController(1);
  controller2.addEventListener('selectstart', onSelectStart)
  controller2.addEventListener('selectend', onSelectEnd)
  scene.add(controller2);

  const controllerModelFactory = new XRControllerModelFactory()

  controllerGrip1 = renderer.xr.getControllerGrip(0)
  controllerGrip1.add(controllerModelFactory.createControllerModel(controllerGrip1))
  scene.add(controllerGrip1)

  controllerGrip2 = renderer.xr.getControllerGrip(1)
  controllerGrip2.add(controllerModelFactory.createControllerModel(controllerGrip2))
  scene.add(controllerGrip2)

//Add line in front of controllers
  const geometry = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, -1)])

  const line = new THREE.Line(geometry)
  line.name = 'line'
  line.scale.z = 15

  controller1.add(line.clone())
  controller2.add(line.clone())

  scene.add(dolly)
  dolly.add(camera, controller1, controller2, controllerGrip1, controllerGrip2)

}


/**
 * Triggered when the VR controller's button is pressed
 */
function onSelectStart(event) {

  const controller = event.target;

  const intersections = getIntersections(controller);
  const intersectionsLetters = getIntersectionsLetters(controller);

  if (intersections.length > 0) {
    const intersection = intersections[0];
    const object = intersection.object;
    transformCameraVR(object)
    hideHelp()
    controller.userData.selected = object;
  }
  if (intersectionsLetters.length > 0) {
    const intersection = intersectionsLetters[0];
    const object = intersection.object;
    findLetter(object)
    controller.userData.selected = object;
  }
}

/**
 * Triggered when the VR controller's button is released
 */
function onSelectEnd(event) {

  const controller = event.target;

  if (controller.userData.selected !== undefined) {

    const object = controller.userData.selected;
    if (object.name === '') {
      transformCameraVR(object)
      hideHelp()
    } else {
      findLetter(object)
    }
    controller.userData.selected = undefined;
  }
}

/**
 * Get intersected tp points with controller ray
 */
function getIntersections(controller) {

  tempMatrix.identity().extractRotation(controller.matrixWorld);

  raycaster.ray.origin.setFromMatrixPosition(controller.matrixWorld);
  raycaster.ray.direction.set(0, 0, -1).applyMatrix4(tempMatrix);

  return raycaster.intersectObjects(TPs);

}

/**
 * Get intersected letters with controller ray
 */
function getIntersectionsLetters(controller) {

  tempMatrix.identity().extractRotation(controller.matrixWorld);

  raycaster.ray.origin.setFromMatrixPosition(controller.matrixWorld);
  raycaster.ray.direction.set(0, 0, -1).applyMatrix4(tempMatrix);

  return raycaster.intersectObjects(textLetters);

}

/**
 * Manage intersection with controller ray
 */
function intersectObjects(controller) {

  // Do not highlight when already selected
  if (controller.userData.selected !== undefined) return

  const line = controller.getObjectByName('line')
  const intersections = getIntersections(controller)
  const intersectionsLetters = getIntersectionsLetters(controller)
  if (intersections.length > 0) {

    const intersection = intersections[0];

    const object = intersection.object
    object.material = tpMaterialHover
    intersected.push(object)

    line.scale.z = intersection.distance

  } else if (intersectionsLetters.length > 0) {
    const intersection = intersectionsLetters[0]
    const object = intersection.object
    intersected.push(object)

    line.scale.z = intersection.distance
  } else {
    line.scale.z = 15;
  }

}

/**
 * Remove objects from the intersected array
 */
function cleanIntersected() {

  while (intersected.length) {

    const object = intersected.pop()
    if (object.name === '')
      object.material = tpMaterial
  }

}

/**
 * Start the animations and update the world each frames
 */
function animate() {

  renderer.setAnimationLoop(render);

}

/**
 * Function to be executed each frame
 */
function render() {
  const elapsedTime = clock.getElapsedTime()
  const deltaTime = elapsedTime - previousTime
  previousTime = elapsedTime

  raycaster.setFromCamera(cursor, camera)

  const intersects = raycaster.intersectObjects(TPs)
  const letterIntersects = raycaster.intersectObjects(textLetters)

  for (const test of TPs) {
    test.material = tpMaterial
    document.body.style.cursor = 'default'
  }
  for (const intersect of intersects) {
    intersect.object.material = tpMaterialHover
    document.body.style.cursor = 'pointer'
  }

  if (intersects.length) {
    currentIntersect = intersects[0]
  } else {
    currentIntersect = null
  }

  if (letterIntersects.length) {
    letterIntersect = letterIntersects[0]
  } else {
    letterIntersect = null
  }

  if(mixerFox)
    mixerFox.update(deltaTime * 0.5)
  if(mixerStag)
    mixerStag.update(deltaTime)

  cleanIntersected();

  intersectObjects(controller1);
  intersectObjects(controller2);

  controls.update()

  renderer.render(scene, camera)
}
